import { useRef, useCallback, useEffect, RefObject } from 'react';
import { PlayerControl } from 'types/Player';

interface UsePlayerTabNavigationProps {
    containerRef: RefObject<HTMLElement | null>;
    holdLayout: (hold: boolean) => void;
    onVolumeButtonClicked: () => void;
}

const isKeyboardFocus = (element: Element) => {
    try {
        return element.matches(':focus-visible');
    } catch {
        return true;
    }
};

export const usePlayerTabNavigation = ({ containerRef, holdLayout, onVolumeButtonClicked }: UsePlayerTabNavigationProps) => {
    // Get the refs of player controls (buttons)
    const controlRefs = useRef(new Map<PlayerControl, HTMLElement>());

    // Set/remove a ref for a player control
    const setControlRef = useCallback(
        (control: PlayerControl) => (element: HTMLElement | null) => {
            if (element) {
                controlRefs.current.set(control, element);
            } else {
                controlRefs.current.delete(control);
            }
        },
        []
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key !== 'Enter') return;

            const focused = Array.from(controlRefs.current.entries()).find(([, element]) => element === document.activeElement);

            if (!focused) return;

            const [control, element] = focused;

            event.preventDefault();
            if (control === PlayerControl.VOLUME) {
                onVolumeButtonClicked();
            } else {
                element.click();
            }
        },
        [onVolumeButtonClicked]
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    useEffect(() => {
        const isInsidePlayer = (node: EventTarget | null) => node instanceof Node && !!containerRef.current?.contains(node);

        const handleFocusIn = (event: FocusEvent) => {
            const { target } = event;

            if (target instanceof Element && target !== containerRef.current && isInsidePlayer(target)) {
                holdLayout(isKeyboardFocus(target));
            }
        };

        const handleFocusOut = (event: FocusEvent) => {
            if (isInsidePlayer(event.target) && !isInsidePlayer(event.relatedTarget)) {
                holdLayout(false);
            }
        };

        document.addEventListener('focusin', handleFocusIn);
        document.addEventListener('focusout', handleFocusOut);
        return () => {
            document.removeEventListener('focusin', handleFocusIn);
            document.removeEventListener('focusout', handleFocusOut);
        };
    }, [containerRef, holdLayout]);

    const focusControl = useCallback((control: PlayerControl) => {
        const element = controlRefs.current.get(control);
        const target = element?.matches('button, [role="button"]') ? element : element?.querySelector<HTMLElement>('button');

        target?.focus();
    }, []);

    return {
        setControlRef,
        focusControl,
    };
};
